import { io, Socket } from 'socket.io-client';
import { API_BASE_URL } from '../config/api';

/**
 * Canal d'observation temps réel (LOG-201).
 *
 * L'Organizer se met à jour tout seul depuis longtemps : il écoute
 * `data_changed` sur le namespace `/sync`. cloud-admin obligeait à recharger
 * la page — le canal existait donc déjà côté serveur, seul l'abonnement
 * manquait. On écoute ici le namespace `/live`, jumeau en lecture seule.
 *
 * AUCUN JETON N'EST MANIPULÉ ICI. Les jetons vivent dans des cookies
 * HTTP-only, illisibles en JavaScript — et c'est voulu. La poignée de main
 * Socket.IO étant une requête HTTP, `withCredentials` suffit à les joindre ;
 * le serveur les lit lui-même.
 */

export interface ChangementDistant {
  entity_type: string;
  action: 'create' | 'update' | 'delete';
  entity_id: string;
  payload?: Record<string, unknown>;
  timestamp?: string;
}

type Ecouteur = (changement: ChangementDistant) => void;

/** `https://events.logfit.eu/api/v1` → `https://events.logfit.eu`. */
function origineServeur(): string {
  try {
    return new URL(API_BASE_URL, window.location.origin).origin;
  } catch {
    return window.location.origin;
  }
}

class RealtimeService {
  private socket: Socket | null = null;
  private organisation: string | null = null;
  private ecouteurs = new Set<Ecouteur>();

  /** Organisation de l'ÉVÉNEMENT affiché — jamais celle de l'utilisateur. */
  connecter(organizationId: string) {
    if (this.socket && this.organisation === organizationId) return;
    this.deconnecter();

    this.organisation = organizationId;
    this.socket = io(`${origineServeur()}/live`, {
      path: '/socket.io',
      transports: ['websocket'],   // le polling passe mal derrière LiteSpeed
      withCredentials: true,
      auth: { organization_id: organizationId },
      reconnection: true,
      reconnectionDelay: 2000,
      reconnectionDelayMax: 30000,
    });

    this.socket.on('data_changed', (changement: ChangementDistant) => {
      this.ecouteurs.forEach((e) => {
        try {
          e(changement);
        } catch (err) {
          // Un écran fautif ne doit pas priver les autres de leurs mises à jour.
          console.warn('[realtime] écouteur en erreur', err);
        }
      });
    });

    this.socket.on('connect_error', (err) => {
      // Le jeton d'accès expire toutes les 15 min : une reconnexion peut être
      // refusée le temps qu'un appel REST le renouvelle. Socket.IO réessaie.
      console.warn('[realtime] connexion refusée:', err.message);
    });
  }

  deconnecter() {
    this.socket?.removeAllListeners();
    this.socket?.disconnect();
    this.socket = null;
    this.organisation = null;
  }

  /** Retourne la fonction de désabonnement. */
  ecouter(ecouteur: Ecouteur): () => void {
    this.ecouteurs.add(ecouteur);
    return () => this.ecouteurs.delete(ecouteur);
  }

  get connecte(): boolean {
    return this.socket?.connected ?? false;
  }
}

export const realtimeService = new RealtimeService();
